import { Command } from 'commander';
import chalk from 'chalk';
import Table from 'cli-table3';
import { ConfigService } from '../../services/config.service.js';
import { ComponentsService } from '../../services/components.service.js';
import { ValidationError } from '../../errors/index.js';
import { getCliName } from '../../utils/cli-name.js';

interface CheckOptions {
  json?: boolean;
}

/**
 * Create check command for Jai1 Framework
 * Checks current version and compares with latest from server
 */
export function createCheckCommand(): Command {
  const cmd = new Command('check')
    .description('Kiểm tra cập nhật framework')
    .option('--json', 'Output as JSON')
    .action(async (options: CheckOptions) => {
      await handleCheck(options);
    });

  return cmd;
}

async function handleCheck(options: CheckOptions): Promise<void> {
  const configService = new ConfigService();
  const config = await configService.load();

  if (!config) {
    throw new ValidationError(`Not initialized. Run "${getCliName()} auth" first.`);
  }

  const componentsService = new ComponentsService();
  const installed = await componentsService.getInstalled();
  const filepaths = Object.keys(installed);

  if (filepaths.length === 0) {
    console.log(
      chalk.yellow(`Chưa có components nào. Chạy "${getCliName()} apply" để bắt đầu.`)
    );
    return;
  }

  console.log(chalk.dim('Đang kiểm tra cập nhật...\n'));

  try {
    const checksums = await componentsService.getChecksums(config, filepaths);
    const updates: string[] = [];
    const upToDate: string[] = [];

    for (const fp of filepaths) {
      const current = installed[fp];
      const latest = checksums[fp];

      if (!latest) {
        // Component removed from server?
        continue;
      }

      if (current.checksum !== latest.checksum) {
        updates.push(fp);
      } else {
        upToDate.push(fp);
      }
    }

    // Output as JSON if requested
    if (options.json) {
      console.log(
        JSON.stringify(
          {
            updates: updates.map((fp) => ({
              path: fp,
              currentVersion: installed[fp].version,
              latestVersion: checksums[fp].version,
            })),
            upToDate: upToDate.length,
            total: filepaths.length,
          },
          null,
          2
        )
      );
      return;
    }

    // Show status table
    console.log(chalk.bold('📌 Trạng thái Components:\n'));

    if (updates.length > 0) {
      const table = new Table({
        head: [
          chalk.bold('Component'),
          chalk.bold('Hiện tại'),
          chalk.bold('Mới nhất'),
          chalk.bold('Trạng thái'),
        ],
        style: {
          head: ['cyan'],
          border: ['gray'],
        },
      });

      for (const fp of updates) {
        const current = installed[fp];
        const latest = checksums[fp];
        table.push([
          fp,
          `v${current.version}`,
          chalk.green(`v${latest.version}`),
          chalk.yellow('⚠️  CẦN CẬP NHẬT'),
        ]);
      }

      console.log(table.toString());
    }

    // Summary
    console.log();
    if (updates.length === 0 && upToDate.length > 0) {
      console.log(
        chalk.green(`✓ Tất cả ${upToDate.length} components đã cập nhật.`)
      );
    } else if (upToDate.length > 0) {
      console.log(chalk.dim(`✓ ${upToDate.length} components đã cập nhật.`));
    }

    if (updates.length > 0) {
      console.log(
        chalk.yellow(`\n⚠️  ${updates.length} component(s) có bản cập nhật!`)
      );
      console.log(
        chalk.dim('Chạy'),
        chalk.cyan(`'${getCliName()} update'`),
        chalk.dim('để cập nhật.')
      );
    } else {
      console.log(chalk.green('\n✅ Mọi thứ đã được cập nhật!'));
    }
  } catch (error) {
    console.error(
      chalk.red('Lỗi kiểm tra cập nhật:'),
      error instanceof Error ? error.message : error
    );
  }
}
